/**
 * mobile-api always answers HTTP 200 with an envelope `{ body, error }`;
 * success and failure differ only by the body. See spec §4.2.
 */

export type FetchFn = (input: string, init: RequestInit & { headers: Record<string, string> }) => Promise<Response>;

/** Business error returned inside the envelope. `code` is upper-case. */
export class ApiError extends Error {
  readonly code: string;
  readonly details?: Record<string, string[]>;
  /** «GET /v1/passes/:id» — the failed request without ids or query, for error reports. */
  endpoint?: string;

  constructor(code: string, message: string, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

/** The request did not produce an envelope: no network, proxy error, broken JSON. */
export class NetworkError extends Error {
  /** «GET /v1/passes/:id» — the failed request without ids or query, for error reports. */
  endpoint?: string;

  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

interface Envelope<T> {
  body: T;
  error: { code: string; message: string; details?: Record<string, string[]> } | null;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string;
  /** A request that hangs longer becomes a NetworkError, so the resident gets «Повторить» instead of a stuck splash. */
  timeoutMs?: number;
}

export const REQUEST_TIMEOUT_MS = 15_000;

/** «/v1/passes/123?x=1» → «GET /v1/passes/:id»: what failed, without ids or parameters. */
export function endpointOf(method: string, path: string): string {
  return `${method} ${path.split('?')[0].replace(/\/\d+(?=\/|$)/g, '/:id')}`;
}

export async function apiRequest<T>(fetchFn: FetchFn, baseUrl: string, path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await send<T>(fetchFn, baseUrl, path, options);
  } catch (error) {
    if (error instanceof ApiError || error instanceof NetworkError) error.endpoint = endpointOf(options.method ?? 'GET', path);
    throw error;
  }
}

async function send<T>(
  fetchFn: FetchFn,
  baseUrl: string,
  path: string,
  { method = 'GET', body, token, timeoutMs = REQUEST_TIMEOUT_MS }: RequestOptions,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new NetworkError(`Timeout after ${timeoutMs} ms`));
    }, timeoutMs);
  });

  let response: Response;
  try {
    // The race also covers a fetch that ignores the abort signal.
    response = await Promise.race([
      fetchFn(`${baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      }),
      timeout,
    ]);
  } catch (cause) {
    clearTimeout(timer);
    if (cause instanceof NetworkError) throw cause;
    throw new NetworkError(cause instanceof Error ? cause.message : 'Request failed');
  }

  if (response.status !== 200) {
    clearTimeout(timer);
    throw new NetworkError(`Unexpected HTTP ${response.status}`);
  }

  let envelope: Envelope<T>;
  try {
    envelope = (await Promise.race([response.json(), timeout])) as Envelope<T>;
  } catch (cause) {
    if (cause instanceof NetworkError) throw cause;
    throw new NetworkError('Response is not JSON');
  } finally {
    clearTimeout(timer);
  }

  if (envelope.error) {
    throw new ApiError(envelope.error.code, envelope.error.message, envelope.error.details);
  }
  return envelope.body;
}
